import { useQuery } from "@tanstack/react-query";
import z from "zod";

import type { FormatStrings } from "@skilitics/localization";

import type { defaultTutorialsState } from "./applicationSettingsAtoms.ts";

export type TutorialArea = keyof typeof defaultTutorialsState;

export type TutorialContent = {
  title: FormatStrings;
  listItems: FormatStrings[];
  actionLabel?: FormatStrings;
  videoUrl?: string;
  imageUrl?: string;
};

type TutorialData = Record<TutorialArea, TutorialContent[]>;

const FormatStringsSchema = z.record(z.string());

const TutorialContentSchema = z.object({
  title: FormatStringsSchema,
  listItems: z.array(FormatStringsSchema),
  actionLabel: FormatStringsSchema.optional(),
  videoUrl: z.string().optional(),
  imageUrl: z.string().optional(),
});

const TutorialDataSchema = z.object({
  moduleMap: z.array(TutorialContentSchema),
  scene: z.array(TutorialContentSchema),
  activity: z.array(TutorialContentSchema),
  responses: z.array(TutorialContentSchema),
});

const TUTORIAL_URL = "https://skilitics.github.io/landing-page/assets/walkthrough.json";

const fetchTutorialDataRaw = async (): Promise<unknown> => {
  const response = await fetch(TUTORIAL_URL);
  if (!response.ok) {
    console.warn(`Failed to fetch tutorial data: ${response.status}`);
    return {}; // Always return a value
  }
  try {
    return (await response.json()) as unknown;
  } catch (e) {
    console.warn("Error parsing Tutorials response JSON: ", e);
    return {};
  }
};

export const useTutorialData = () => {
  const { data, ...rest } = useQuery({
    queryKey: ["tutorial"],
    queryFn: fetchTutorialDataRaw,
    staleTime: Infinity,
    retry: false,
  });

  let parsed: TutorialData | undefined = undefined;
  if (data) {
    try {
      parsed = TutorialDataSchema.parse(data);
    } catch (e) {
      console.warn("Tutorials JSON is invalid: ", e);
    }
  }

  return { data: parsed, ...rest };
};
